import React from 'react';
import { GitBranch, Compass, Quote } from 'lucide-react';

/**
 * HarmonyPrinciple — the philosophical method behind the chemistry.
 *
 * Where ProtocolOfHarmony names *what* cooperates inside the bottle,
 * HarmonyPrinciple names *how* the house reasons: by analogy.
 *
 * The Vedas observed that nature does not legislate uniformity — it
 * sustains harmony through difference. We carry that observation
 * across domains: a plant's UV defense becomes the strand's UV armor;
 * a cell membrane's lipid bridge becomes a formula's emulsion;
 * the panchamahabhuta's five-system biology becomes a five-system
 * regimen. Analogy is not metaphor here — it is the engine.
 */

const ANALOGIES = [
  {
    overline: 'Domain I · Plant',
    natural: "Rice bran shields its embryo with ferulic acid.",
    arrow:   '→',
    applied: "Rice Bran Oil shields the hair strand from UV.",
  },
  {
    overline: 'Domain II · Cell',
    natural: "Phosphatidylcholine builds every cell membrane — a head that loves water, a tail that loves oil.",
    arrow:   '→',
    applied: "Sunflower Lecithin bridges Kewra Water and the oil phase, holding the emulsion without heat.",
  },
  {
    overline: 'Domain III · Cosmos',
    natural: "The Vedas name five elements — earth, water, fire, air, ether — each with its hour of openness.",
    arrow:   '→',
    applied: "Atreya prescribes the ritual against the hour that the corresponding system is most receptive.",
  },
];

const HarmonyPrinciple = ({ testId = 'harmony-principle' }) => (
  <section
    id="harmony-principle"
    data-testid={testId}
    className="relative px-6 md:px-12 py-24 md:py-32 bg-[#7C9CB1] text-[#F4F8F9] border-t border-[#F4F8F9]/15"
  >
    <div className="max-w-[1400px] mx-auto">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 mb-14">
        <div className="md:col-span-7">
          <p
            data-testid={`${testId}-overline`}
            className="font-mono text-[11px] md:text-[12px] tracking-[0.34em] uppercase font-semibold text-[#D4AF5A] mb-6 flex items-center gap-3"
          >
            <span aria-hidden className="inline-block w-10 h-[2px] bg-[#D4AF5A]" />
            The Manifesto · § III
          </p>
          <h2
            data-testid={`${testId}-title`}
            className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.02]"
          >
            The Harmony Principle.
            <br />
            <em className="text-[#F4F8F9]/80">Analogical Reasoning.</em>
          </h2>
        </div>
        <div className="md:col-span-5 md:pt-4">
          <p
            data-testid={`${testId}-thesis`}
            className="font-serif italic text-lg md:text-xl leading-relaxed max-w-md text-[#F4F8F9]/90"
          >
            Nature does not legislate uniformity — it sustains harmony
            through difference. Our method is to listen for the same
            pattern in three places: a plant, a cell, a cosmos — and
            translate one into the next. Analogy is not metaphor here.
            It is the engine.
          </p>
        </div>
      </div>

      <ol
        data-testid={`${testId}-analogies`}
        className="space-y-6 md:space-y-8"
      >
        {ANALOGIES.map((a, i) => (
          <li
            key={a.overline}
            data-testid={`${testId}-analogy-${i}`}
            className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-center border-t border-[#F4F8F9]/20 pt-6 md:pt-8"
          >
            <p className="md:col-span-2 font-mono text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-[#D4AF5A]">
              {a.overline}
            </p>
            <p className="md:col-span-4 font-serif italic text-lg md:text-xl text-[#F4F8F9]/90 leading-snug">
              {a.natural}
            </p>
            <p
              aria-hidden
              className="hidden md:block md:col-span-1 font-mono text-2xl text-[#D4AF5A] text-center"
            >
              {a.arrow}
            </p>
            <p className="md:col-span-5 font-serif text-lg md:text-xl text-[#F4F8F9] leading-snug">
              {a.applied}
            </p>
          </li>
        ))}
      </ol>

      <div
        data-testid={`${testId}-closing`}
        className="mt-14 md:mt-20 pt-10 border-t border-[#F4F8F9]/20 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16"
      >
        <div className="md:col-span-7">
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#F4F8F9]/70 mb-4 flex items-center gap-3">
            <Compass size={13} strokeWidth={1.5} className="text-[#D4AF5A]" />
            The Method
          </p>
          <p className="font-serif text-xl md:text-2xl leading-snug text-[#F4F8F9]/90">
            The Vedas named the pattern · the spectrometer confirms it ·
            the bottle inherits it. From this triangle we make every
            decision — which botanical to refuse, which hour to prescribe,
            which name to put on the label.
          </p>
        </div>
        <aside className="md:col-span-5">
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#F4F8F9]/70 mb-4 flex items-center gap-3">
            <Quote size={13} strokeWidth={1.5} className="text-[#D4AF5A]" />
            On nature
          </p>
          <p className="font-serif italic text-xl text-[#F4F8F9]/90 leading-snug">
            &ldquo;What is sustained, sustains by likeness. Find the
            likeness. Honour the difference.&rdquo;
          </p>
          <p className="mt-3 font-mono text-[10px] tracking-[0.32em] uppercase text-[#F4F8F9]/55 flex items-center gap-2">
            <GitBranch size={11} strokeWidth={1.5} className="text-[#D4AF5A]" />
            Atelier doctrine · MMXXV
          </p>
        </aside>
      </div>
    </div>
  </section>
);

export default HarmonyPrinciple;
